import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAPContext } from "@/lib/gsap-context";

export function useSplitText(options = {}) {
  const ref = useRef(null);
  const { reducedMotion } = useGSAPContext();

  const {
    type = "words",
    y = 60,
    rotateX = -30,
    duration = 0.9,
    ease = "power3.out",
    stagger = 0.04,
    start = "top 85%",
    once = true,
  } = options;

  useEffect(() => {
    if (reducedMotion || !ref.current) return;

    const el = ref.current;
    const original = el.innerHTML;
    const text = el.textContent || "";

    let pieces = [];
    if (type === "chars") {
      pieces = text.split("");
    } else if (type === "words") {
      pieces = text.trim().split(/\s+/);
    } else {
      pieces = text.split(/(?<=\.\s+|\n)/);
    }

    const wrapped = pieces
      .map((piece) => {
        if (piece === " ") return " ";
        return `<span class="split-piece inline-block overflow-hidden"><span class="split-inner inline-block">${piece}</span></span>`;
      })
      .join(type === "chars" ? "" : " ");

    el.innerHTML = wrapped;

    const inners = el.querySelectorAll(".split-inner");

    gsap.set(inners, { y, rotateX, opacity: 0, transformOrigin: "center bottom" });

    const tween = gsap.to(inners, {
      y: 0,
      rotateX: 0,
      opacity: 1,
      duration,
      ease,
      stagger,
      scrollTrigger: {
        trigger: el,
        start,
        toggleActions: once ? "play none none none" : "play reverse play reverse",
      },
    });

    return () => {
      tween.kill();
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger === el) st.kill();
      });
      if (el) el.innerHTML = original;
    };
  }, [reducedMotion, type, y, rotateX, duration, ease, stagger, start, once]);

  return ref;
}
